// Nova sala (canvas F03 e T02): nome, ingresso (Grátis ou Tentos) e regras da casa. Toda sala é pública: aparece na
// lista de salas abertas, e quem tem o link ou o código também entra. Prata e Ouro mudam as regras e criam sala
// valendo Tentos; os outros planos criam com as regras padrão e veem o cadeado. A partida vai sempre até 12.
// Mesa da comunidade (comunidade): qualquer membro cria; só os membros veem e entram.
import { useRef, useState } from 'react';
import { DEFAULT_HOUSE_RULES, type HouseRules, type SignalVisibility } from '@truco6/regras';
import { MENSAGEM_NOME, validarNomeDeSala } from '@truco6/moderacao';
import { INGRESSOS_TENTOS, NOME_DE_SALA_MAX, NOME_DO_PLANO, calcularPote, planosQueTem, pode } from '@truco6/planos';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { criarSala } from '../mesa/criar.ts';
import { VISIBILIDADE_DOS_SINAIS } from '../mesa/textos.ts';
import { Tento, usePainelTentos } from './tentos-comum.tsx';
import { NOTA_SEM_RANKING } from '../liga/comum.tsx';
import { Tela, Topo } from './comum.tsx';

const TEMPOS = [15_000, 20_000, 30_000, 45_000];

function Opcoes<T extends string | number>({ rotulo, valor, opcoes, travado, aoMudar, largo = false }: {
  rotulo: string; valor: T; opcoes: [T, React.ReactNode][]; travado: boolean; aoMudar: (v: T) => void; largo?: boolean;
}) {
  return (
    <div className={'segmentos' + (largo ? ' largo' : '')} role="radiogroup" aria-label={rotulo}>
      {opcoes.map(([v, nome]) => (
        <button key={String(v)} role="radio" aria-checked={valor === v} disabled={travado} className={valor === v ? 'on' : undefined}
          onClick={() => aoMudar(v)}>{nome}</button>
      ))}
    </div>
  );
}

function Chave({ rotulo, ligada, travado, aoMudar }: { rotulo: string; ligada: boolean; travado: boolean; aoMudar: (v: boolean) => void }) {
  return (
    <button role="switch" aria-checked={ligada} aria-label={rotulo} disabled={travado} className={'chave' + (ligada ? ' on' : '')}
      onClick={() => aoMudar(!ligada)}><i /></button>
  );
}

export function CriarSala({ sessao, perfil, ingressoInicial = null, comunidade = null }: {
  sessao: Sessao; perfil: PerfilConta; ingressoInicial?: number | null; comunidade?: { id: string; nome: string } | null;
}) {
  const [regras, setRegras] = useState<HouseRules>({ ...DEFAULT_HOUSE_RULES });
  const [nome, setNome] = useState(() => (comunidade ? comunidade.nome : `Mesa de ${perfil.apelido}`).slice(0, NOME_DE_SALA_MAX));
  const [erroNome, setErroNome] = useState<string | null>(null);
  const campoNome = useRef<HTMLInputElement>(null);
  const livre = pode(perfil.plano, 'regrasDaCasa');
  const [tentos, setTentos] = useState(ingressoInicial !== null);
  const [valor, setValor] = useState<number>(ingressoInicial ?? 6);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const { painel } = usePainelTentos(sessao);
  const podeTentos = pode(perfil.plano, 'criarSalaComTentos');
  const valendo = tentos && podeTentos;
  const travado = !livre || criando;
  const mudar = <K extends keyof HouseRules>(k: K, v: HouseRules[K]) => setRegras((r) => ({ ...r, [k]: v }));


  // O que vale de verdade: valendo Tentos não tem bot; a mesa da comunidade usa sempre a zoeira ligada.
  const zoeiraFixa = !!comunidade;
  const efetivas: HouseRules = { ...regras, ...(valendo ? { vagasComBot: false } : {}), ...(zoeiraFixa ? { zoeira: true } : {}) };
  const pote = calcularPote(valor);
  const disponivel = painel ? Math.max(0, painel.disponivel) : null;
  const faltam = valendo && disponivel !== null ? Math.max(0, valor - disponivel) : 0;

  const linhas: [string, string, React.ReactNode][] = [
    ['Pontos para vencer', 'Partida até 12, em qualquer sala', <b className="valor-fixo">12</b>],
    ['Tempo por jogada', 'Relógio de cada um na vez',
      <Opcoes rotulo="Tempo por jogada" valor={efetivas.tempoPorJogadaMs} travado={travado} aoMudar={(v) => mudar('tempoPorJogadaMs', v)}
        opcoes={TEMPOS.map((ms) => [ms, ms / 1000 + 's'])} />],
    ['Sinais', 'O que os adversários veem',
      <Opcoes rotulo="Sinais" largo valor={efetivas.sinais} travado={travado} aoMudar={(v) => mudar('sinais', v)}
        opcoes={(['desligados', 'aviso', 'todos'] as const).map((x) => [x, VISIBILIDADE_DOS_SINAIS[x]] as [SignalVisibility, string])} />],
    ['Zoeira', comunidade ? 'Mesa da comunidade usa sempre ligada' : 'Frases para zoar o trio adversário',
      <Opcoes rotulo="Zoeira" valor={efetivas.zoeira ? 'ligada' : 'desligada'} travado={travado || zoeiraFixa} aoMudar={(v) => mudar('zoeira', v === 'ligada')}
        opcoes={[['ligada', 'Ligada'], ['desligada', 'Desligada']]} />],
    ['Vagas vazias com bot', valendo ? 'Valendo Tentos começa com 6 pessoas' : 'O bot sai quando alguém chega',
      <Chave rotulo={'Vagas vazias com bot' + (valendo ? ', desligado e travado' : '')} ligada={efetivas.vagasComBot} travado={travado || valendo}
        aoMudar={(v) => mudar('vagasComBot', v)} />],
  ];

  async function criar() {
    setErro(null);
    // O mesmo filtro da mesa: avisa antes de mandar.
    const v = validarNomeDeSala(nome);
    if (v !== 'ok') { setErroNome(MENSAGEM_NOME[v]); campoNome.current?.focus(); return; }
    setCriando(true);
    try {
      // Sem plano, nem manda regras: a sala nasce com as padrão.
      const codigo = await criarSala(sessao, perfil, {
        regras: livre ? efetivas : undefined, nome: nome.trim() || undefined, publica: !comunidade, ingresso: valendo ? valor : null,
        ...(comunidade ? { comunidade: comunidade.id } : {}),
      });
      medir('criar_sala', { regras_da_casa: livre, publica: !comunidade, ingresso: valendo ? valor : 0, ...(comunidade ? { comunidade: true } : {}) });
      ir({ tela: 'sala', codigo }, true);
    } catch (e) {
      const msg = (e as Error).message;
      // Sem partidas grátis hoje: quem cria senta, e a mesa recusa. A tela Limite explica.
      if ((e as { codigo?: string }).codigo === 'sem_partidas') { ir({ tela: 'limite' }); return; }
      if ((Object.values(MENSAGEM_NOME) as string[]).includes(msg)) { setErroNome(msg); campoNome.current?.focus(); } else setErro(msg);
      setCriando(false);
    }
  }

  return (
    <Tela>
      <div className="topo-com-selo">
        <Topo titulo={comunidade ? 'Mesa da comunidade' : 'Nova sala'} voltar={voltar} />
        <span className={'selo-plano plano-' + perfil.plano}>{NOME_DO_PLANO[perfil.plano]}</span>
      </div>
      <div className="duas-colunas nova-sala">
        <div className="col">
          <label className="campo">
            <span>Nome da sala</span>
            <input ref={campoNome} value={nome} maxLength={NOME_DE_SALA_MAX} autoComplete="off" disabled={criando}
              aria-invalid={erroNome ? true : undefined} aria-describedby={erroNome ? 'erro-nome' : undefined}
              onChange={(ev) => { setNome(ev.target.value); setErroNome(null); }} />
            {erroNome && <small id="erro-nome" className="erro" role="alert">{erroNome}</small>}
          </label>
          <section aria-labelledby="h-quem" className="campo">
            <h2 id="h-quem" className="rotulo">Quem entra</h2>
            <p className="cartao nota-comunidade">
              {comunidade
                ? <>Só os membros da <b>{comunidade.nome}</b> veem e entram.</>
                : 'A sala aparece na lista de salas abertas, para qualquer um entrar. Quem tem o link ou o código também entra.'}
            </p>
          </section>
          <section aria-labelledby="h-plateia" className="campo">
            <h2 id="h-plateia" className="rotulo">Plateia</h2>
            <p className="cartao nota-comunidade">
              {comunidade ? 'Os membros assistem.' : 'Qualquer conta assiste.'} Até 20 pessoas, que veem só o que está na mesa: nunca as cartas da mão,
              os sinais ou os votos. Você pode tirar alguém da plateia.
            </p>
          </section>
          <section aria-labelledby="h-ingresso">
            <h2 id="h-ingresso" className="rotulo">Ingresso</h2>
            <div className={'cartao ingresso' + (valendo ? ' valendo' : '')}>
              <Opcoes rotulo="Ingresso da sala" largo valor={valendo ? 'tentos' : 'gratis'} travado={criando}
                aoMudar={(v) => setTentos(v === 'tentos')}
                opcoes={[['gratis', 'Grátis'], ['tentos', <span className="com-tento"><Tento tamanho={16} vinco={false} />Tentos</span>]]} />
              {tentos && !podeTentos && (
                <div className="cadeado-tentos">
                  <p className="cadeado">Sala valendo Tentos é dos planos {planosQueTem('criarSalaComTentos')}.</p>
                  <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>Ver planos</button>
                </div>
              )}
              {valendo && (
                <>
                  <div className="linha-valores">
                    <span>Tentos por jogador</span>
                    <Opcoes rotulo="Tentos por jogador" valor={valor} travado={criando} aoMudar={setValor}
                      opcoes={INGRESSOS_TENTOS.map((n) => [n, String(n)])} />
                  </div>
                  <div className="previa-pote" aria-label={`Prévia: pote ${pote.pote} Tentos, casa fica com ${pote.casa}, cada vencedor leva ${pote.premioPorVencedor}`}>
                    <div><span>POTE</span><b>{pote.pote}</b></div>
                    <div><span>CASA</span><b>{pote.casa}</b></div>
                    <div><span>VENCEDOR</span><b className="dourado">{pote.premioPorVencedor}</b></div>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
        <div className="col">
          <section aria-labelledby="h-regras">
            <h2 id="h-regras" className="rotulo">Regras da casa</h2>
            <ul className={'cartao regras-casa' + (livre ? '' : ' travadas')}>
              {linhas.map(([titulo, sub, controle]) => (
                <li key={titulo}><p><b>{titulo}</b><span>{sub}</span></p>{controle}</li>
              ))}
            </ul>
          </section>
          {!livre && (
            <div className="cartao">
              <p className="cadeado">Regras da casa são dos planos {planosQueTem('regrasDaCasa')}. Sua sala sai com as regras padrão.</p>
              <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>Ver planos</button>
            </div>
          )}
          {valendo && (
            <p className="nota com-icone nota-ingresso">
              <Tento tamanho={18} vinco={false} />
              <span>Você também paga {valor}. Saldo disponível: <b>{disponivel ?? '...'} Tentos</b>. O ingresso fica reservado até a partida começar.</span>
            </p>
          )}
          {erro && <p className="erro" role="alert">{erro}</p>}
          <div className="rodape-cta">
            {faltam > 0
              ? <button className="btn1 largo" onClick={() => ir({ tela: 'loja' })}>FALTAM {faltam} TENTOS</button>
              : <button className="btn1 largo" disabled={criando} onClick={criar}>
                {criando ? 'CRIANDO A SALA...' : valendo ? `CRIAR E CONVIDAR · ${valor}` : 'CRIAR E CONVIDAR'}
              </button>}
            <p className="nota centro">{faltam > 0 ? 'Compre Tentos na loja e volte para criar.' : 'O link sai pronto para o WhatsApp.'}</p>
            {efetivas.vagasComBot && <p className="nota centro">{NOTA_SEM_RANKING}</p>}
          </div>
        </div>
      </div>
    </Tela>
  );
}
